import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, RowStyle } from "ag-grid-community";
import { useTranslation } from "react-i18next";
import PaginationSizeSelector from "../trips/columnDefComponents/PaginationSizeSelector";
import {
  SendTripDocumentResponse,
  TripPODResponse,
  TripPODResponseEndirchedDocs,
  TripStop,
} from "../../types/api";
import { agGridStyle } from "../trips/style";
import StatusCellRenderer from "../trips/columnDefComponents/StatusCellRenderer";
import StatusCellRendererDocuments from "./components/StatusCellRendererDocuments";
import { Box, Button, Typography } from "@mui/material";
import NotePopoverRenderer from "./components/NotePopoverRenderer";
import uploadRedIcon from "../../assets/images/icons/uploadRed.svg";
import { useTripDocument } from "../../context/TripDocumentContext";
interface AgGridDocumentsProps {
  documents: any;
  stops: TripStop[];
  openSidebar?: any;
  openGridSidebar: (documentData: any, action: boolean) => void;
}

const AgGridDocuments: React.FC<AgGridDocumentsProps> = ({
  documents,
  stops,
  openSidebar,
  openGridSidebar,
}) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>(null);
  const [paginationPageSize, setPaginationPageSize] = useState(50);
  function areAllLoadingPointsEqual(
    docs: TripPODResponseEndirchedDocs[]
  ): boolean {
    // Filtra i documenti con COMBO221_131 diverso da "POD"
    const filteredDocs = docs.filter((doc) => doc.COMBO221_131 === "POD");

    // Se non ci sono documenti validi, escludiamo il controllo e restituiamo true
    if (filteredDocs.length === 0) {
      return true;
    }

    // Prendiamo il valore di loadingPoint del primo documento valido
    const referenceLoadingPoint = filteredDocs[0].loadingPoint;

    // Controlliamo se tutti i documenti filtrati hanno lo stesso loadingPoint
    const allEqual = filteredDocs.every(
      (doc) => doc.loadingPoint === referenceLoadingPoint
    );

    return allEqual;
  }
  const [areLoadingPointsEqual, setAreLoadingPointsEqual] = useState<
    boolean | null
  >(null);
  useEffect(() => {
    // Calcola il valore al caricamento della pagina
    const result = areAllLoadingPointsEqual(documents);
    setAreLoadingPointsEqual(result);
  }, [documents]); // Dipendenza: si aggiorna quando `docs` cambia

  const { getDocumentDetails } = useTripDocument();
  const handleOpenFilePreview = async (documentId: string) => {
    try {
      // Recupera i dettagli del documento
      const documentDetails = await getDocumentDetails(documentId);

      // Crea un Blob dal binario
      const pdfBlob = new Blob([documentDetails], { type: "application/pdf" });

      // Crea un URL temporaneo per il Blob
      const url = URL.createObjectURL(pdfBlob);

      // Apre il PDF in una nuova scheda

      window.open(url, "_blank");
    } catch (error) {
      console.error(
        "Errore durante il recupero o la visualizzazione del documento:",
        error
      );
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Category",
        field: "COMBO221_131",
        minWidth: 100,
        resizable: false,
        
      },
      {
        headerName: "Description",
        field: "DOCNAME",
        minWidth: 140,
        
        cellRenderer: (params: any) => {
          // Controlla se lo stato del documento è "rejected"
          if (params.data?.DOCNUMBER)
            return (
              <Button
                onClick={() =>
                  handleOpenFilePreview(String(params.data?.DOCNUMBER || ""))
                }
                variant="text"
                sx={{
                  padding: 0,
                  margin: 0,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "text.primary",
                  textDecoration: "underline",
                }}
              >
                {params.value}
              </Button>
            );
          else return "-";
        },
      },
      {
        headerName: "File name",
        field: "ORIGINALE",
        minWidth: 140,
        
      },
      {
        headerName: "Loading Point",
        field: "loadingPoint",
        minWidth: 220,
        
      },
      {
        headerName: "Unloading Point",
        field: "unloadingPoint",
        minWidth: 220,
        
      },
      {
        headerName: "Uploaded",
        field: "DATA224_131",
        minWidth: 150,
        
        valueFormatter: (params: any) => {
          if (!params.value) return ""; // Gestisce il caso di valore nullo o non definito

          const date = new Date(params.value);

          // Verifica se la data è valida
          if (isNaN(date.getTime())) return params.value;

          // Ottieni l'offset del fuso orario in minuti e convertilo in millisecondi
          const timezoneOffset = date.getTimezoneOffset() * 60000;

          // Aggiungi l'offset UTC (Italia: Europe/Rome)
          const localTime = new Date(date.getTime() - timezoneOffset);

          // Formatta data e ora
          const formattedDate = localTime.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const formattedTime = localTime.toLocaleTimeString("it-IT", {
            hour: "2-digit",
            minute: "2-digit",
          });

          return `${formattedDate} ${formattedTime}`;
        },
      },

      {
        headerName: "Status",
        field: "STATO",
        minWidth: 160,
        
        cellRenderer: (params: any) => (
          <div
            style={{
              height: "40px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            {params.data.CLASSEDOC === "OPE.ALLGEN" ? (
              <Typography>-</Typography>
            ) : (
              <StatusCellRendererDocuments value={params.value} />
            )}
          </div>
        ),
      },
      {
        headerName: "Action",
        field: "",
        minWidth: 190,
        
        cellRenderer: (params: any) => {
          if (
            params.data.STATO === "Annullato" ||
            params.data.STATO === "DEFAULT" ||
            params.data.STATO === "Errore" ||
            params.data.STATO === "Errore"
          ) {
            return (
              <Button
                onClick={() => {
                  // Prepara i dati del documento per la sidebar della grid
                  const documentData = {
                    loadingPoint: params.data.loadingPoint,
                    unloadingPoint: params.data.unloadingPoint,
                    loadingTmsLocation: params.data.loadingTmsLocation,
                    unloadingTmsLocation: params.data.unloadingTmsLocation,
                    transportOrderTMSKey: params.data.transportOrderTMSKey,
                    involvedPartyName: params.data.involvedPartyName,
                    invPartyName: params.data.invPartyName,
                    tmsObjectNumber: params.data.tmsObjectNumber
                  };
                  
                  // Log unico per il debug della grid
                  console.log('[DEBUG GRID] Upload Requested:', {
                    // Dati originali dalla grid
                    originalData: {
                      loadingPoint: params.data.loadingPoint,
                      unloadingPoint: params.data.unloadingPoint,
                      loadingTmsLocation: params.data.loadingTmsLocation,
                      unloadingTmsLocation: params.data.unloadingTmsLocation,
                      transportOrderTMSKey: params.data.transportOrderTMSKey,
                      involvedPartyName: params.data.involvedPartyName,
                      invPartyName: params.data.invPartyName,
                      tmsObjectNumber: params.data.tmsObjectNumber,
                      STATO: params.data.STATO,
                      DOCNUMBER: params.data.DOCNUMBER
                    },
                    // Dati preparati per la sidebar
                    documentData,
                    // Stringa concatenata che verrà usata
                    expectedConcatenatedString: `${params.data.loadingTmsLocation}_${params.data.unloadingTmsLocation}`,
                    // Contesto dell'azione
                    action: false,
                    source: 'grid_upload_button'
                  });
                  
                  openGridSidebar(documentData, false);
                }}
                variant="text"
                sx={{
                  padding: 0,
                  margin: 0,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
                startIcon={
                  <img
                    src={uploadRedIcon}
                    alt="Documents Icon"
                    style={{
                      width: "20px",
                      height: "20px",
                      minWidth: "20px",
                      minHeight: "20px",
                    }}
                  />
                }
              >
                Upload Requested
              </Button>
            );
          }
          return null; // Non renderizza nulla se lo stato non è "rejected"
        },
      },

      {
        headerName: "Note",
        field: "MULTI336_142",
        minWidth: 80,
        resizable: false,
        cellRenderer: (params: any) => (
          <NotePopoverRenderer value={params.value} />
        ),
      },
    ],
    [t]
  );

  const defaultColDef: ColDef = {
    minWidth: 80,
    resizable: true,
    

    flex: 1,
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationPageSize(newPageSize);
  };

  const getRowClass = (params: any): string | undefined => {
    if (params.data.STATO === "Annullato" || params.data.STATO === "DEFAULT") {
      return "highlight-row"; // Nome della classe CSS
    }
    return undefined;
  };

  const localeText = {
    page: t("screens.trips.agGrid.page"),
    nextPage: t("screens.trips.agGrid.nextPage"),
    previousPage: t("screens.trips.agGrid.previousPage"),
    lastPage: t("screens.trips.agGrid.lastPage"),
    firstPage: t("screens.trips.agGrid.firstPage"),
    ariaPaginationPage: t("screens.trips.agGrid.ariaPaginationPage"),
    ariaPagination: t("screens.trips.agGrid.ariaPagination"),
    ariaLabelPagination: t("screens.trips.agGrid.ariaLabelPagination"),
    rangeSelectionEmpty: t("screens.trips.agGrid.rangeSelectionEmpty"),
    paginationSummary: t("screens.trips.agGrid.paginationSummary"),
    empty: t("screens.trips.agGrid.empty"),
    loadingOoo: t("screens.trips.agGrid.loadingOoo"),
    to: t("screens.trips.agGrid.to"),
    of: t("screens.trips.agGrid.of"),
  };

  return (
    <>
      <div
        className="ag-theme-alpine"
        style={{ height: "100%", width: "100%", overflowX: "auto" }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={documents}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={paginationPageSize}
          domLayout="autoHeight"
          getRowClass={getRowClass}
          localeText={localeText}
        />
        <div style={{ width: "100%", height: "4px" }}>
          <PaginationSizeSelector
            currentPageSize={paginationPageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </>
  );
};

export default AgGridDocuments;
